// NOTE: When adding/removing/renaming a slug here, mirror the change in
// .github/scripts/investor-types.json and update EXPECTED_SLUGS in
// .github/scripts/generate-investor-type-content.js. The generation
// script asserts slug parity and fails the workflow on drift.

export interface InvestorTypeEntry {
  slug: string;
  /** Value used in the ILIKE filter against `investors.investor_type`. */
  typeValue: string;
  label: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
}

export const INVESTOR_TYPES: InvestorTypeEntry[] = [
  {
    slug: "corporate-venture-capital",
    typeValue: "Corporate VC",
    label: "Corporate Venture Capital",
    metaTitle: "Corporate Venture Capital (CVC) Investors | CAPLINK",
    metaDescription:
      "Browse Corporate Venture Capital investors on CAPLINK. Find CVCs actively backing startups — filtered by sector, stage and geography.",
    h1: "Corporate Venture Capital investors",
    intro:
      "Corporate Venture Capital (CVC) arms invest on behalf of their parent corporation — bringing strategic value beyond capital. Browse verified CVC investors on CAPLINK and filter by sector, stage and geography.",
  },
  {
    slug: "family-office",
    typeValue: "Family Office",
    label: "Family Office",
    metaTitle: "Family Office Investors for Startups | CAPLINK",
    metaDescription:
      "Find Family Office investors actively backing startups on CAPLINK. Patient capital, flexible terms — filter by sector, stage and geography.",
    h1: "Family Office investors",
    intro:
      "Family offices invest personal wealth with more flexibility than institutional VCs — no fund lifecycle, no LP pressure. Browse verified family office investors on CAPLINK.",
  },
  {
    slug: "venture-capital",
    typeValue: "VC",
    label: "Venture Capital",
    metaTitle: "Venture Capital Investors | CAPLINK",
    metaDescription:
      "Browse verified Venture Capital investors on CAPLINK. Filter VC firms by stage, sector, geography and check size.",
    h1: "Venture Capital investors",
    intro:
      "Venture Capital firms are the primary institutional capital source for high-growth startups. Browse verified VC investors on CAPLINK and filter by stage, sector and geography.",
  },
  {
    slug: "angel",
    typeValue: "Business Angel",
    label: "Angel Investors",
    metaTitle: "Angel Investors for Startups | CAPLINK",
    metaDescription:
      "Find Angel investors actively backing startups on CAPLINK. Filter by sector, stage and geography to find the right angel for your round.",
    h1: "Angel investors",
    intro:
      "Angel investors provide early-stage capital from personal funds — often faster and more flexible than institutional VCs. Browse verified angel investors on CAPLINK.",
  },
  {
    slug: "private-equity",
    typeValue: "PE/Buy-Out",
    label: "Private Equity",
    metaTitle: "Private Equity Investors | CAPLINK",
    metaDescription:
      "Browse Private Equity and Buy-Out investors on CAPLINK. Filter by sector, stage and geography.",
    h1: "Private Equity investors",
    intro:
      "Private Equity firms invest in later-stage, established companies through buyouts and growth capital. Browse verified PE investors on CAPLINK.",
  },
  {
    slug: "accelerator",
    typeValue: "Incubator, Accelerator",
    label: "Accelerator & Incubator",
    metaTitle: "Startup Accelerator & Incubator Investors | CAPLINK",
    metaDescription:
      "Find Accelerators and Incubators investing in startups on CAPLINK. Filter by sector, stage and geography.",
    h1: "Accelerator & Incubator investors",
    intro:
      "Accelerators and incubators provide capital, mentorship and network in exchange for equity. Browse verified accelerator programs on CAPLINK.",
  },
  {
    slug: "government",
    typeValue: "Government-backed",
    label: "Government & Public Investors",
    metaTitle: "Government & Public Startup Investors | CAPLINK",
    metaDescription:
      "Find government-backed investors and public funding bodies on CAPLINK. Filter by region and sector.",
    h1: "Government & public investors",
    intro:
      "Government-backed funds and public investment bodies provide non-dilutive and co-investment capital for startups. Browse verified public investors on CAPLINK.",
  },
];

export const investorTypeFromSlug = (slug: string | undefined): InvestorTypeEntry | undefined =>
  INVESTOR_TYPES.find((t) => t.slug === slug);
